/**
 * "Couldn't check" reason codes — V109-1-10 / LM-9 / ADR-0088 (amended 2026-10-13).
 *
 * Reviewed code next to the enabler catalog (changes by PR, like the catalog). Every
 * result GAC Pro could not judge — a check row in `error`, `insufficient_evidence` or
 * `stale`, a failing source — carries exactly one code from this closed set. Each code
 * has plain words (what users see), who can fix it, and the enabler that fixes it, so a
 * Couldn't-check row always has an action (no dead ends). A Couldn't-check result is never
 * counted as pass or as fail; a control with any in-scope resource in this state is not met
 * yet, and an assessor export reports it as not-satisfied with the words in its remarks.
 *
 * `reasonFromCollectorError` maps a collector's error text (Google, AWS, Azure, SaaS APIs)
 * to a code: 401 / expired → credential_expired, 403 / denied → access_denied,
 * SERVICE_DISABLED / not activated → api_not_enabled, 429 / quota → rate_limited, page or
 * budget caps → read_truncated; anything else → collector_error.
 *
 * Pure data + pure functions; no runtime imports (check-imports).
 */
import { connectCodeFor, getEnablerCatalogEntry } from './catalog.js';
import { vendorName } from './progress.js';
/**
 * V109-1-19 (H7): `mapping_unapproved` — GAC Pro has nothing approved to compare the read
 * against yet (the GCP policy-mapping library waits in the review queue). It used to be filed
 * as `unsupported`, whose fix (attach evidence) never cleared it.
 * V109-1-19 R3 (R2-B2, owner decision 2026-10-13): GAC Pro owns it — its editors approve the
 * mappings, so customers get no Setup step for it; what they CAN do is attach evidence instead.
 */
export const COULDNT_CHECK_REASONS = [
    'access_denied', 'api_not_enabled', 'read_truncated', 'rate_limited', 'collector_error',
    'credential_expired', 'stale', 'odp_unset', 'missing_measurement', 'not_connected', 'unsupported',
    'mapping_unapproved',
];
export function isCouldntCheckReason(v) {
    return typeof v === 'string' && COULDNT_CHECK_REASONS.includes(v);
}
export const FIXER_WORDS = {
    source_admin: 'An admin of the connected service',
    engineering: 'Your engineering team',
    compliance_lead: 'Your compliance lead',
    gac_pro: 'GAC Pro retries on its own',
};
/** The reviewed taxonomy (one entry per code, in COULDNT_CHECK_REASONS order). */
export const COULDNT_CHECK_TAXONOMY = [
    { code: 'access_denied', words: "GAC Pro's read-only access can't see this", fixer: 'source_admin', enabler: 'fix:reconnect', fallback_enabler: 'fix:reconnect' },
    { code: 'api_not_enabled', words: 'The service API GAC Pro reads is turned off', fixer: 'source_admin', enabler: 'fix:enable-api', fallback_enabler: 'fix:enable-api' },
    { code: 'read_truncated', words: 'The list was too long to read in one pass', fixer: 'gac_pro', enabler: 'fix:collect-again', fallback_enabler: 'fix:collect-again' },
    { code: 'rate_limited', words: 'The service asked GAC Pro to slow down', fixer: 'gac_pro', enabler: 'fix:collect-again', fallback_enabler: 'fix:collect-again' },
    { code: 'collector_error', words: 'The last collection hit an error', fixer: 'gac_pro', enabler: 'fix:collect-again', fallback_enabler: 'fix:collect-again' },
    { code: 'credential_expired', words: 'The connection’s sign-in expired', fixer: 'source_admin', enabler: 'fix:reconnect', fallback_enabler: 'fix:reconnect' },
    { code: 'stale', words: 'The last reading is too old to count', fixer: 'gac_pro', enabler: 'fix:collect-again', fallback_enabler: 'fix:collect-again' },
    { code: 'odp_unset', words: 'A value your organization must choose isn’t set', fixer: 'compliance_lead', enabler: 'fix:set-parameters', fallback_enabler: 'fix:set-parameters' },
    { code: 'missing_measurement', words: 'The tool reported a result without the measured value', fixer: 'engineering', enabler: 'fix:send-measurement', fallback_enabler: 'fix:send-measurement' },
    { code: 'not_connected', words: 'The source for this evidence isn’t connected', fixer: 'source_admin', enabler: 'connect:{kind}', fallback_enabler: 'fix:connect-source' },
    { code: 'unsupported', words: 'GAC Pro can’t check this automatically yet', fixer: 'compliance_lead', enabler: 'fix:attach-evidence', fallback_enabler: 'fix:attach-evidence' },
    { code: 'mapping_unapproved', words: 'GAC Pro is still approving the settings to compare against', fixer: 'gac_pro', enabler: 'fix:attach-evidence', fallback_enabler: 'fix:attach-evidence',
        customer_step: false, fixer_words: 'GAC Pro approves these; nothing to fix on your side', content_batch: 'gcp-mappings' },
];
/** V109-1-19 R3 (R2-B2): the code has a customer Setup step (GAC Pro-owned codes have none). */
export function hasCustomerStep(code) {
    return couldntCheckEntry(code).customer_step !== false;
}
const BY_CODE = new Map(COULDNT_CHECK_TAXONOMY.map((e) => [e.code, e]));
export function couldntCheckEntry(code) {
    return BY_CODE.get(code);
}
/**
 * The catalog code that fixes `code` for a source of `connectorKind` (always a catalog code).
 * V109-1-19 R3 (R2-B1): a connection that isn't collecting decides a connection fix — one GAC Pro
 * turned to `error` needs a new sign-in (fix:reconnect: "collect again" would never run, GAC Pro
 * doesn't schedule it), and a `disabled` one needs turning back on (fix:turn-on). Fixes that are
 * not about the connection (a parameter, a measurement, evidence) are unchanged.
 */
export function enablerForReason(code, connectorKind, status) {
    const e = couldntCheckEntry(code);
    let out;
    if (e.enabler.includes('{kind}')) {
        const resolved = connectorKind ? connectCodeFor(connectorKind) : null;
        out = resolved && getEnablerCatalogEntry(resolved) ? resolved : e.fallback_enabler;
    }
    else
        out = e.enabler;
    if (status === 'disabled' && getEnablerCatalogEntry(out)?.subject === 'connector')
        return 'fix:turn-on';
    if (status === 'error' && out === 'fix:collect-again')
        return 'fix:reconnect';
    return out;
}
/**
 * V109-1-19 (H4): one label per fix, naming the source when it is known. Setup's step title,
 * Today's strip and every Couldn't-check note use it, so one fix reads the same everywhere.
 * V109-1-19 R3 (R2-H2/H3): `subjectLabel` names a control ("AC-2") or a test for the fixes about
 * one control or one test; the source name only ever names a connection.
 */
export function fixLabel(enablerCode, sourceName, subjectLabel) {
    const title = getEnablerCatalogEntry(enablerCode)?.title ?? enablerCode;
    const subject = subjectLabel?.trim();
    if (subject && enablerCode === 'fix:set-parameters')
        return `Set your organization's values for ${subject}`;
    if (subject && enablerCode === 'fix:attach-evidence')
        return `Attach evidence for ${subject}`;
    const name = sourceName?.trim();
    if (!name)
        return title;
    switch (enablerCode) {
        case 'fix:reconnect': return `Reconnect ${name}`;
        case 'fix:enable-api': return `Turn on the ${name} API GAC Pro reads`;
        case 'fix:collect-again': return `Collect from ${name} again`;
        case 'fix:turn-on': return `Turn ${name} back on`;
        default: return title;
    }
}
/** V109-1-19 R3 (R2-B2): what the row offers for a GAC Pro-owned reason ("Attach evidence instead"). */
export const INSTEAD_LABEL = 'Attach evidence instead';
/** The plain name of a connector kind ("gcp" → "Google Cloud"); null when unknown. */
export function sourceNameOf(kind) {
    return kind ? vendorName(kind) : null;
}
export function couldntCheckView(code, connectorKind, sourceName, opts = {}) {
    const e = couldntCheckEntry(code);
    const enabler = enablerForReason(code, connectorKind, opts.status);
    const customer = e.customer_step !== false;
    // R2-B1: a connection in error or turned off is never collected again on its own; once its
    // status turns the fix into a reconnect / turn-on, an admin of the service fixes it, not GAC Pro.
    const statusFix = e.fixer === 'gac_pro' && (enabler === 'fix:reconnect' || enabler === 'fix:turn-on');
    const fixer = statusFix ? 'source_admin' : e.fixer;
    return {
        reason_code: code, words: e.words, fixer, fixer_words: statusFix ? FIXER_WORDS.source_admin : e.fixer_words ?? FIXER_WORDS[e.fixer],
        enabler_code: enabler, enabler_title: getEnablerCatalogEntry(enabler)?.title ?? enabler,
        fix_label: customer ? fixLabel(enabler, sourceName ?? sourceNameOf(connectorKind), opts.subjectLabel) : INSTEAD_LABEL,
        customer_step: customer,
    };
}
/**
 * V109-1-19 (B1): the subject the fix's Setup step is computed for — the same key
 * `loadCouldntCheckFixes` writes — so a fix link opens THAT step, never another source's.
 * `connector:<id>` for a fix on one connection, `system:<id>` for a CI measurement,
 * `kind:<kind>` for a connect step, else `workspace`. Null when the subject can't be named
 * (a connection fix without a connection id).
 * V109-1-19 R3 (R2-H2/H3): `control:<program>/<requirement_id>` for a value one control needs, and
 * `test:<test_key>` for evidence one test needs; each falls back to `workspace` when the source
 * row doesn't name its control or test.
 */
export function fixSubjectKey(enablerCode, ctx) {
    const entry = getEnablerCatalogEntry(enablerCode);
    if (!entry)
        return null;
    switch (entry.subject) {
        case 'workspace': return 'workspace';
        case 'system': return ctx.system_id ? `system:${ctx.system_id}` : null;
        case 'connector_kind': return ctx.kind ? `kind:${ctx.kind}` : null;
        case 'connector': return ctx.connector_id ? `connector:${ctx.connector_id}` : null;
        case 'control': return ctx.program && ctx.requirement_id ? controlSubject(ctx.program, ctx.requirement_id) : 'workspace';
        case 'test': return ctx.test_key ? `test:${ctx.test_key}` : 'workspace';
        default: return null;
    }
}
/** `control:<program>/<requirement_id>` (the requirement id may itself hold a colon: `nist-800-53:ac-2`). */
export function controlSubject(program, requirementId) {
    return `control:${program}/${requirementId}`;
}
export function parseControlSubject(subjectKey) {
    if (!subjectKey.startsWith('control:'))
        return null;
    const rest = subjectKey.slice('control:'.length);
    const i = rest.indexOf('/');
    return i > 0 && i < rest.length - 1 ? { program: rest.slice(0, i), requirement_id: rest.slice(i + 1) } : null;
}
export function parseDisabledApi(text) {
    const s = String(text ?? '');
    if (!s || reasonFromCollectorError(s) !== 'api_not_enabled')
        return null;
    const service = /\b([a-z0-9-]+\.googleapis\.com)\b/.exec(s)?.[1] ?? null;
    const name = /\b((?:[A-Z][A-Za-z0-9]*\s){0,5}API)\s+has not been used/.exec(s)?.[1]?.trim()
        ?? /\b((?:[A-Z][A-Za-z0-9]*\s){1,5})is not enabled\b/.exec(s)?.[1]?.trim() ?? null;
    const project = /\bproject[s]?[ /]+([a-z][a-z0-9-]{4,61}[a-z0-9]|\d{6,})\b/.exec(s)?.[1] ?? null;
    if (!service && !name)
        return null;
    const console_url = service
        ? `https://console.cloud.google.com/apis/library/${encodeURIComponent(service)}${project ? `?project=${encodeURIComponent(project)}` : ''}`
        : null;
    return { service, name, project, console_url };
}
/** Check-row statuses that are Couldn't check (never pass, never fail). */
export const COULDNT_CHECK_STATUSES = ['error', 'insufficient_evidence', 'stale'];
export function isCouldntCheckStatus(status) {
    return COULDNT_CHECK_STATUSES.includes(status);
}
/**
 * A collector's error text → a code. Order matters: an expired sign-in (401) before a denied
 * read (403), a disabled API before a generic 403/400 (Google answers 403 SERVICE_DISABLED),
 * quota before a generic failure.
 */
export function reasonFromCollectorError(error) {
    const s = String(error ?? '');
    // V112-2-7 (BL-25): Azure admin consent removed / the tenant no longer linked means reconnect (checked before the
    // generic "refused" of access_denied, which the consent-gone message also contains).
    if (/admin consent (?:may have been |was |has been )?(?:removed|revoked)|no longer linked to this connection/i.test(s))
        return 'credential_expired';
    if (/\b401\b|unauthenticated|authentication error|expired|revoked|invalid_grant|invalid[_ ]token|token (?:is )?(?:revoked|invalid)|sign-in for the service account failed|rejected the (?:service account )?(?:client|credential|key|token)/i.test(s))
        return 'credential_expired';
    // V112-2-7: Azure subscriptions readable by nobody = GAC Pro's app lacks its Reader role assignments.
    if (/none of the subscriptions is readable|assign GAC Pro.{0,3}s app Reader/i.test(s))
        return 'access_denied';
    if (/SERVICE_DISABLED|BILLING_DISABLED|has not been used in project|is not activated|has not been activated|API (?:is )?(?:not enabled|disabled)|not enabled|permanently disabled|is disabled/i.test(s))
        return 'api_not_enabled';
    if (/\b429\b|RESOURCE_EXHAUSTED|rate[ -]?limit|quota|throttl|too many requests|slow down/i.test(s))
        return 'rate_limited';
    if (/\b403\b|PERMISSION_DENIED|AccessDenied|AuthorizationFailed|Forbidden|not authorized|missing (?:the )?(?:role|permission)|(?:role|account|key|token) is missing|insufficient (?:permission|scope|privilege)|RequestDenied|access denied|refused|cannot read|can't read|not permitted/i.test(s))
        return 'access_denied';
    if (/truncat|stopped after|page (?:cap|budget|limit)|budget (?:exhausted|ran out)|not read to the end|partial read/i.test(s))
        return 'read_truncated';
    if (/not connected|no (?:connector|connection)|connection is not set up/i.test(s))
        return 'not_connected';
    if (/no approved .*(?:polic|mapping)|approve the .*mapping/i.test(s))
        return 'mapping_unapproved';
    if (/not supported|unsupported/i.test(s))
        return 'unsupported';
    return 'collector_error';
}
/**
 * The code for a check row GAC Pro could not judge, when the writer gave none:
 * an explicit `reason_code` in details, else from its error text, else from the status
 * (`stale` → stale, an incomplete read → read_truncated, an unmeasured tool verdict →
 * missing_measurement). `error` without text → collector_error.
 */
export function defaultCouldntCheckReason(status, details) {
    if (!isCouldntCheckStatus(status))
        return null;
    const d = details ?? {};
    if (isCouldntCheckReason(d.reason_code))
        return d.reason_code;
    if (status === 'stale')
        return 'stale';
    if (typeof d.error === 'string' && d.error)
        return reasonFromCollectorError(d.error);
    if (status === 'error')
        return 'collector_error';
    if (d.complete === false)
        return 'read_truncated';
    return 'missing_measurement';
}
/** Problems with the taxonomy (empty = valid). The CI test runs this. */
export function validateCouldntCheckTaxonomy() {
    const v = [];
    const codes = COULDNT_CHECK_TAXONOMY.map((e) => e.code);
    if (codes.join() !== COULDNT_CHECK_REASONS.join())
        v.push('taxonomy must list every code once, in order');
    for (const e of COULDNT_CHECK_TAXONOMY) {
        if (!e.words.trim() || e.words.length > 70)
            v.push(`${e.code}: words must be plain (1..70 characters)`);
        if (/[_{}0-9]/.test(e.words))
            v.push(`${e.code}: words must not carry codes`);
        if (!FIXER_WORDS[e.fixer])
            v.push(`${e.code}: unknown fixer`);
        if (!getEnablerCatalogEntry(e.fallback_enabler))
            v.push(`${e.code}: fallback enabler ${e.fallback_enabler} is not a catalog code`);
        if (!e.enabler.includes('{kind}') && !getEnablerCatalogEntry(e.enabler))
            v.push(`${e.code}: enabler ${e.enabler} is not a catalog code`);
        // V109-1-19 R3 (R2-B2): only GAC Pro can own a fix (no customer step); its enabler is still a
        // catalog code — the action a customer can take instead — so the row is never a dead end.
        if (e.customer_step === false && e.fixer !== 'gac_pro')
            v.push(`${e.code}: only a gac_pro fix may have no customer step`);
        if (e.fixer_words !== undefined && (!e.fixer_words.trim() || e.fixer_words.length > 70 || /[_{}0-9]/.test(e.fixer_words)))
            v.push(`${e.code}: fixer words must be plain (1..70 characters)`);
    }
    return v;
}
//# sourceMappingURL=couldnt-check.js.map